// 🔧 security_and_trust.js
// ✅ Purpose: Public-facing manager guide for security and trust in Let's Go Vote
// ✅ Features:
// - Explains authentication, email verification, permissions, and audit concepts
// - Helps managers understand trust-building features
// - Public and SEO-friendly
// - Uses the Dashboard-style LGV layout
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../../components/seo_head";

function SecurityAndTrustGuide() {
  return (
    <section id="top-content" className="container-fluid py-5">
      <SeoHead
        title="Security and Trust Guide | Let's Go Vote"
        description="Learn how Let's Go Vote supports event trust through secure sign-in, email verification, event permissions, audit records, and vote integrity features."
        path="/guides/security-and-trust"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Guides", path: "/guides" },
          {
            name: "Security & Trust",
            path: "/guides/security-and-trust",
          },
        ]}
      />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🛡️ Security & Trust</h1>
          <p className="lead text-muted">
            Understand how Let&apos;s Go Vote supports trusted event voting.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <section className="mb-5">
          <div className="alert alert-info text-start">
            <h5 className="mb-3">Trust matters when people vote</h5>
            <p className="mb-0">
              Let&apos;s Go Vote is built around authenticated access,
              email verification, event-level permissions, audit records, and
              clear voting workflows.
            </p>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Secure sign-in</h3>
          <p>
            Users sign in through secure authentication. Supported sign-in
            options may include email and password, Google sign-in, and Facebook
            sign-in depending on the current app configuration.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Email verification</h3>
          <p>
            Email verification helps reduce fraudulent account creation and
            gives voters a confirmed account identity before participating in
            protected voting flows.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Event-level permissions</h3>
          <p>
            Event managers, volunteers, voters, and administrators can have
            different access levels. This helps ensure users only access the
            tools and event data appropriate for their role.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Audit records</h3>
          <p>
            Audit records help event organizers review voting activity and
            investigate concerns. A visible audit process increases confidence
            when results are announced.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Vote integrity</h3>
          <p>
            Let&apos;s Go Vote is designed to support clear voting rules,
            authenticated users, event-specific permissions, and results review
            before winners are published.
          </p>
        </section>

        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Related guide</h5>
            <p className="mb-3">
              Event-day preparation helps managers protect confidence in the
              final results.
            </p>

            <Link to="/guides/running-event-day" className="btn btn-primary">
              Review Running Event Day
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default SecurityAndTrustGuide;